import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Menu, X, MessageCircle } from 'lucide-react';
import { useQuery, gql } from '@apollo/client';

const GET_UNREAD_COUNT = gql`
  query GetUnreadMessageCount {
    unreadMessageCount
  }
`;

const Navbar = ({
  currentUser,
  currentPath = '/student/dashboard',
  brandLogoUrl,
  primaryColor,
  secondaryColor,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { data: unreadData } = useQuery(GET_UNREAD_COUNT, {
    skip: !currentUser,
    pollInterval: 30000,
  });

  const unreadCount = unreadData?.unreadMessageCount || 0;

  const colors = useMemo(() => ({
    primary: primaryColor || '#5489B6',
    secondary: secondaryColor || '#002949',
  }), [primaryColor, secondaryColor]);

  const isActive = (path) => currentPath === path;

  const linkStyle = (path) => ({
    color: isActive(path) ? colors.primary : undefined,
  });

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <a href="/student" className="flex-shrink-0 flex items-center">
              <img
                src={brandLogoUrl || 'https://s3-us-west-2.amazonaws.com/findbob-production/images/findbob-grey-blue-nobob-150x50.png'}
                alt="Logo"
                className="h-10 w-auto"
              />
            </a>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            {!currentUser ? (
              <>
                <a
                  href="/student"
                  className="text-sm font-medium transition-colors text-gray-700 hover:opacity-80"
                >
                  Login
                </a>
                <a href="/student/signup">
                  <button
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                    style={{ backgroundColor: colors.primary }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                  >
                    Sign Up
                  </button>
                </a>
              </>
            ) : (
              <>
                <a
                  href="/student/dashboard"
                  className="text-sm font-medium transition-colors"
                  style={isActive('/student/dashboard') ? { color: colors.primary } : {}}
                  onMouseEnter={(e) => { if (!isActive('/student/dashboard')) e.currentTarget.style.color = colors.primary; }}
                  onMouseLeave={(e) => { if (!isActive('/student/dashboard')) e.currentTarget.style.color = ''; }}
                >
                  Dashboard
                </a>
                <span data-tour="nav-profile">
                  <a
                    href="/student/profile"
                    className="text-sm font-medium transition-colors"
                    style={isActive('/student/profile') ? { color: colors.primary } : {}}
                    onMouseEnter={(e) => { if (!isActive('/student/profile')) e.currentTarget.style.color = colors.primary; }}
                    onMouseLeave={(e) => { if (!isActive('/student/profile')) e.currentTarget.style.color = ''; }}
                  >
                    My Profile
                  </a>
                </span>
                <a
                  href="/student/messages"
                  data-tour="nav-messages"
                  className="text-sm font-medium transition-colors flex items-center gap-1.5 relative"
                  style={isActive('/student/messages') ? { color: colors.primary } : {}}
                  onMouseEnter={(e) => { if (!isActive('/student/messages')) e.currentTarget.style.color = colors.primary; }}
                  onMouseLeave={(e) => { if (!isActive('/student/messages')) e.currentTarget.style.color = ''; }}
                >
                  <MessageCircle className="h-4 w-4" />
                  Messages
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full min-w-[18px]">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </a>

                {/* User Menu */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm"
                      style={{ backgroundColor: colors.primary }}
                    >
                      {currentUser.name?.charAt(0) || 'U'}
                    </div>
                    <span className="text-sm font-medium text-gray-700">
                      {currentUser.name?.split(' ')[0] || 'Student'}
                    </span>
                  </div>
                  <a
                    href="/logout"
                    data-turbo-method="delete"
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Logout
                  </a>
                </div>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none"
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200">
          <div className="pt-2 pb-3 space-y-1 px-2">
            {!currentUser ? (
              <>
                <a
                  href="/student"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
                >
                  Login
                </a>
                <a
                  href="/student/signup"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
                >
                  Sign Up
                </a>
              </>
            ) : (
              <>
                <a
                  href="/student/dashboard"
                  className="block px-3 py-2 rounded-md text-base font-medium"
                  style={isActive('/student/dashboard') ? { backgroundColor: `${colors.primary}10`, color: colors.primary } : {}}
                >
                  Dashboard
                </a>
                <a
                  href="/student/profile"
                  className="block px-3 py-2 rounded-md text-base font-medium"
                  style={isActive('/student/profile') ? { backgroundColor: `${colors.primary}10`, color: colors.primary } : {}}
                >
                  My Profile
                </a>
                <a
                  href="/student/messages"
                  className="block px-3 py-2 rounded-md text-base font-medium relative"
                  style={isActive('/student/messages') ? { backgroundColor: `${colors.primary}10`, color: colors.primary } : {}}
                >
                  <span className="flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" />
                    Messages
                    {unreadCount > 0 && (
                      <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </span>
                </a>
                <div className="px-3 py-2">
                  <div className="flex items-center gap-2 mb-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm"
                      style={{ backgroundColor: colors.primary }}
                    >
                      {currentUser.name?.charAt(0) || 'U'}
                    </div>
                    <span className="text-sm font-medium text-gray-700">
                      {currentUser.name || 'Student'}
                    </span>
                  </div>
                  <a
                    href="/logout"
                    data-turbo-method="delete"
                    className="block w-full px-4 py-2 text-center text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Logout
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

Navbar.propTypes = {
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    email: PropTypes.string,
  }),
  currentPath: PropTypes.string,
  brandLogoUrl: PropTypes.string,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
};

export default Navbar;
